/**
 * Pure model of the type-ahead combobox (`./Combobox`, task T.R4) — the matching
 * and the keyboard arithmetic, framework-free on purpose: the `unit` Playwright
 * project runs in plain Node, where a component file would drag React along (the
 * same rule `quotas-model.ts` states).
 *
 * Nothing here knows about venues: the caller decides what an option's searchable
 * text is (the quotas page searches the venue name AND its address).
 */

export interface ComboboxOption {
  /** The value `onChange` hands back (the venue id as text, on the quotas page). */
  value: string;
  /** The primary line — what the input shows once the option is selected. */
  label: string;
  /** Secondary line, searchable but not displayed in the input. */
  hint?: string;
}

/**
 * Folds the two things a RU search has to fold: the case and ё/е. The venue
 * dictionary is user-entered, so «ул. Ёлкина» and «ёлкина» must meet; the
 * whitespace collapse keeps a double space in a name from hiding a match.
 */
export function normalizeForSearch(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/\s+/g, ' ')
    .trim();
}

/** How well an option answers the query — the lower, the higher it lists. */
export type ComboboxMatchTier = 0 | 1 | 2 | -1;

/**
 * `0` the name starts with the query («Топки» → «…Топки»), `1` the name merely
 * contains it, `2` only the secondary text (the address) matches, `-1` no match.
 */
export function matchTier(option: ComboboxOption, query: string): ComboboxMatchTier {
  const needle = normalizeForSearch(query);
  if (needle === '') return 0;
  const label = normalizeForSearch(option.label);
  if (label.startsWith(needle)) return 0;
  if (label.includes(needle)) return 1;
  if (normalizeForSearch(option.hint ?? '').includes(needle)) return 2;
  return -1;
}

/** `true` when the option answers the query at all (name OR secondary text). */
export function matchesComboboxQuery(option: ComboboxOption, query: string): boolean {
  return matchTier(option, query) !== -1;
}

/**
 * The open listbox: matching options first by match quality, then in the order
 * the caller gave (the venue dictionary's id order). An empty query returns the
 * whole dictionary untouched — the list the user scrolls before typing.
 */
export function filterComboboxOptions(options: readonly ComboboxOption[], query: string): ComboboxOption[] {
  return options
    .map((option, index) => ({ option, index, tier: matchTier(option, query) }))
    .filter((entry) => entry.tier !== -1)
    .sort((a, b) => a.tier - b.tier || a.index - b.index)
    .map((entry) => entry.option);
}

/** Where the highlight goes for a navigation key — clamped, never wrapping. */
export function nextActiveIndex(current: number, key: 'ArrowDown' | 'ArrowUp' | 'Home' | 'End', count: number): number {
  if (count === 0) return -1;
  switch (key) {
    case 'ArrowDown':
      return Math.min(current + 1, count - 1);
    case 'ArrowUp':
      return Math.max(current - 1, 0);
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
  }
}
